import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { PDFDownloadLink } from "@react-pdf/renderer";
import { useSelector, useDispatch } from "react-redux";
import {
  Button,
  Table,
  Select,
  Form,
  Pagination,
  DatePicker,
  Dropdown,
  Menu,
  Space,
  message,
} from "antd";
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  DownOutlined,
  FilePdfOutlined,
  FileTextOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import type { RangePickerProps } from "antd/es/date-picker";
import type { PaginationProps, MenuProps } from "antd";
import moment from "moment";
import { RootState } from "../../redux/rootReducer";
import {
  ContributorType,
  Currency,
  DateFormat,
  PageSize,
  ROLE,
  TrackIncomeKeys,
} from "../../shared/constant";
import { retrieveLabels } from "../../redux/actions/labelActions";
import {
  downloadTracksInCSV,
  resetDownloadTracksInCSV,
  resetTracks,
  retrieveAllTracks,
  retrieveTracks,
  retrieveTracksIncomeSummary,
} from "../../redux/actions/trackActions";
import { retrieveContributors } from "../../redux/actions/contributorActions";
import retrievePlatforms from "../../redux/actions/platformActions";
import {
  AllIncomeCLAPAdminCLAPManagerColumn,
  AllIncomeColumn,
  AllIncomeForAccountsColumn,
  ClientIncomeColumn,
} from "../../shared/table/columns";
import tableUtils from "../../utils/tableUtils";
import validateMessages from "../../utils/validate";
import CreateInvoiceModal from "../AccountDetails/CreateInvoiceModal";
import InvoiceConfirmationModal from "../AccountDetails/InvoiceConfirmationModal";
import retrieveCurrencies from "../../redux/actions/currencyActions";
import { getClientFailedShares, resetClientFailedShares } from "../../redux/actions/clientFailedSharesActions";
import SearchComponent from "./SearchComponent";
import globalUtils from "../../utils/globalUtils";
import { getSingleClient } from "../../redux/actions/accountActions";
import ClientProfile from "./ClientProfile";
import PDFRenderedDoc from "../PDF/PDFRenderedDoc";
import { isAccounts, isClapAdminORClapManager } from "../../utils/clapUtils";
import { getDistributors } from "../../redux/actions/distributorActions";
import dateUtils from "../../utils/dateUtils";

const { Option } = Select;
const { RangePicker } = DatePicker;

export const getSelectFields = (name: string, options: any) => {
  return (
    <div>
      <Form.Item
        name={name === "Label" ? `${name.toLowerCase()}s` : name.toLowerCase()}
        label={name}
        className="mb-[6px]"
      >
        <Select
          mode="multiple"
          placeholder={`Select ${name}`}
          className="w-full"
          // showSearch
          allowClear
          filterOption={(inputValue, option) =>
            option?.props.children
              .toString()
              .toLowerCase()
              .includes(inputValue.toLowerCase())
          }
        >
          {options?.map((option: any) => (
            <Option key={option.id} value={option.id}>
              {name === "Track" ? option.title : option.name}
            </Option>
          ))}
        </Select>
      </Form.Item>
    </div>
  );
};

// previous quarter date
const previousQuarterStartDate = moment()
  .subtract(1, "quarter")
  .startOf("quarter")
  .format(DateFormat.DEFAULT);

const previousQuarterEndDate = moment()
  .subtract(1, "quarter")
  .endOf("quarter")
  .format(DateFormat.DEFAULT);

const initialDateRangeState = {
  from: previousQuarterStartDate,
  to: previousQuarterEndDate,
};

function ClapIncomes({
  isClientDetails,
  clientDetails,
}: {
  isClientDetails?: Boolean;
  clientDetails?: any;
}) {
  const dispatch = useDispatch();
  const { userDetails } = useSelector((state: RootState) => state.user);
  const { clientId } = useParams();
  const [page, setPage] = useState(1);
  const [searchText, setSearchText] = useState("");
  const [isIncomeData, setIsIncomeData] = useState(false);
  const [visible, setVisible] = useState(false);
  const [confirmVisible, setConfirmVisible] = useState(false);
  const [isApiCallInProgress, setIsApiCallInProgress] = useState(false);
  const [currency, setCurrency] = useState<string>(Currency.EUR);
  const [currency2, setCurrency2] = useState<string>(Currency.EUR);
  const [dateRange, setDateRange] = useState(initialDateRangeState);
  const [dateRangeOpen, setDateRangeOpen] = useState(false);
  const [dateRangeExceed, setDateRangeExceed] = useState(false);
  const [clientPayableSort, setClientPayableSort] = useState("-payable");
  const [effect, setEffect] = useState(false);
  const {
    tracks,
    tracksResponse,
    allTracks,
    allTracksLoading,
    tablePending,
    tracksIncomeSummary,
    tracksIncomeSummaryPending,
    downloadTracksInCSVPending,
    downloadedTracksInCSVResponse,
  } = useSelector((state: RootState) => state.track);
  const { allLabels } = useSelector((state: RootState) => state.label);
  const { allArtists, allLyricists, allComposers } = useSelector(
    (state: RootState) => state.contributor,
  );
  const { allPlatforms } = useSelector((state: RootState) => state.platform);
  const { allDistributors } = useSelector(
    (state: RootState) => state.distributor,
  );
  const { currencies } = useSelector((state: RootState) => state.currency);
  const { client: clientApiDetails } = useSelector(
    (state: RootState) => state.account,
  );
  const { failedShares, pending: failedSharesPending } = useSelector(
    (state: RootState) => state.clientFailedShares,
  );

  const [filterForm] = Form.useForm();
  const [form] = Form.useForm();
  const [columns, setColumns] = useState<any[]>([]);
  const [client, setClient] = useState<any>(null);

  useEffect(() => {
    if (clientApiDetails) {
      setClient(clientApiDetails);
    } else if (isClientDetails) {
      setClient(clientDetails);
    }
  }, [clientApiDetails]);

  useEffect(() => {
    if (isClientDetails) {
      setColumns(ClientIncomeColumn);
      return;
    }
    if (isClapAdminORClapManager(userDetails)) {
      setColumns(AllIncomeCLAPAdminCLAPManagerColumn);
      return;
    }
    if (isAccounts(userDetails)) {
      dispatch(getSingleClient(userDetails?.client));
      setColumns(AllIncomeForAccountsColumn);
      return;
    }
    setColumns(AllIncomeColumn);
  }, []);

  useEffect(() => {
    dispatch(resetTracks());
  }, []);

  useEffect(() => {
    dispatch(
      retrieveTracksIncomeSummary({
        ...(isClientDetails && {
          clientId,
          reason: "client_report_for_invoice",
        }),
        ...(!isClientDetails &&
          isClapAdminORClapManager(userDetails) && {
          reason: "clap_report",
        }),
        ...(isAccounts(userDetails) && {
          clientId: userDetails.client,
        }),
        ...filterForm.getFieldsValue(),
        currency,
        dateRange,
      }),
    );
  }, [effect]);

  useEffect(() => {
    if (isIncomeData) {
      dispatch(
        retrieveTracks({
          ...(isClientDetails && { clientId }),
          ...(isAccounts(userDetails) && {
            clientId: userDetails.client,
          }),
          ...(!isClientDetails &&
            isClapAdminORClapManager(userDetails) && {
            clapReport: "clap-report",
          }),
          page,
          searchText,
          ...filterForm.getFieldsValue(),
          currency,
          dateRange,
          ...(isClientDetails && { clientPayableSort }),
        }),
      );
    }
  }, [page, effect, clientPayableSort, isIncomeData]);

  useEffect(() => {
    dispatch(retrieveCurrencies());

    dispatch(retrieveLabels({ clientId, page: "all" }));
    dispatch(
      retrieveContributors({
        clientId,
        type: ContributorType.ARTIST,
        page: "all",
      }),
    );
    dispatch(
      retrieveContributors({
        clientId,
        type: ContributorType.LYRICIST,
        page: "all",
      }),
    );
    dispatch(
      retrieveContributors({
        clientId,
        type: ContributorType.COMPOSER,
        page: "all",
      }),
    );
    dispatch(
      retrievePlatforms({
        page: "all",
        ...(isClientDetails && { clientId }),
        ...(isAccounts(userDetails) && {
          clientId: userDetails.client,
        }),
      }),
    );
    dispatch(
      getDistributors({
        page: "all",
        ...(isClientDetails && { clientId }),
        ...(isAccounts(userDetails) && {
          clientId: userDetails.client,
        }),
      }),
    );
  }, []);

  const onApplyFilters = async () => {
    setCurrency2(currency);
    setSearchText("");
    setPage(1);
    setEffect(!effect);
  };

  const resetFilters = () => {
    filterForm.resetFields();
    form.resetFields();
    setDateRange(initialDateRangeState);
    setCurrency(Currency.EUR);
    setCurrency2(Currency.EUR);
    setPage(1);
    setEffect(!effect);
    setDateRangeExceed(false);
  };

  const handleSearch = (value: string) => {
    setSearchText(value);
    setPage(1);
    setEffect(!effect);
  };

  const handlePageChange: PaginationProps["onChange"] = (p) => {
    setPage(p);
  };

  const createInvoice = async () => {
    if (clientId) {
      // Prevent multiple API calls
      if (!isApiCallInProgress) {
        setIsApiCallInProgress(true);
        // Fetch failed shares data first
        dispatch(getClientFailedShares({ clientId }));
      }
    } else {
      // If no clientId, proceed directly to invoice creation
      // Only open if tracksIncomeSummary is available
      if (tracksIncomeSummary) {
        setVisible(true);
      }
    }
  };

  const handleCreateInvoice = () => {
    // Reset failed shares state and close confirmation modal
    dispatch(resetClientFailedShares());
    setConfirmVisible(false);

    // Only open the invoice modal if tracksIncomeSummary is available
    if (tracksIncomeSummary) {
      setVisible(true);
    }
  };

  const hideModal = () => {
    setVisible(false);
  };

  const onRangeChange: RangePickerProps["onChange"] = (dates, dateStrings) => {
    setDateRange({ from: dateStrings[0], to: dateStrings[1] });
    setPage(1);

    setDateRangeExceed(
      !dateUtils.checkEndWithinOneYearSameMonth(
        moment(dateStrings[0], DateFormat.DEFAULT),
        moment(dateStrings[1], DateFormat.DEFAULT),
      ),
    );
  };

  const onRangeOpenChange = (open: boolean) => {
    setDateRangeOpen(open);
    if (open) {
      setDateRangeExceed(false);
      form.setFieldsValue({ income_period: [null, null] });
      setDateRange({ from: "", to: "" });
    }
  };

  const onCurrencyChange = (value: string) => {
    if (value) setCurrency(value);
    else setCurrency("");
    setPage(1);
  };

  const getPayableAmount = (c: string) => {
    switch (c) {
      case Currency.BDT:
        return tracksIncomeSummary?.total_payable_bdt;
      case Currency.EUR:
        return tracksIncomeSummary?.total_payable_eur;
      case Currency.USD:
        return tracksIncomeSummary?.total_payable_usd;

      default:
        return "";
    }
  };

  const checkPayableZero = () => {
    const formFields = form.getFieldsValue();
    if (formFields.currency) {
      const val = getPayableAmount(formFields.currency);
      if (!val) return true;
      if (val > 0) return false;
      return true;
    }
    return true;
  };

  const getFirstTotal = {
    income: function income(c: string) {
      switch (c) {
        case Currency.USD:
          if (isClientDetails)
            return tracksIncomeSummary?.client_total_income_usd || 0;
          if (isClapAdminORClapManager(userDetails))
            return tracksIncomeSummary?.clap_total_income_usd || 0;
          return tracksIncomeSummary?.client_total_income_usd || 0;
        case Currency.EUR:
          if (isClientDetails)
            return tracksIncomeSummary?.client_total_income_eur || 0;
          if (isClapAdminORClapManager(userDetails))
            return tracksIncomeSummary?.clap_total_income_eur || 0;
          return tracksIncomeSummary?.client_total_income_eur || 0;
        case Currency.BDT:
          if (isClientDetails)
            return tracksIncomeSummary?.client_total_income_bdt || 0;
          if (isClapAdminORClapManager(userDetails))
            return tracksIncomeSummary?.clap_total_income_bdt || 0;
          return tracksIncomeSummary?.client_total_income_bdt || 0;

        default:
          return 0;
      }
    },
    payable: function payable(c: string) {
      switch (c) {
        case Currency.USD:
          return tracksIncomeSummary?.total_payable_usd || 0;
        case Currency.EUR:
          return tracksIncomeSummary?.total_payable_eur || 0;

        case Currency.BDT:
          return tracksIncomeSummary?.total_payable_bdt || 0;

        default:
          return 0;
      }
    },
    labelReceived: function labelReceived(c: string) {
      switch (c) {
        case Currency.USD:
          return tracksIncomeSummary?.track_total_net_income_usd || 0;
        case Currency.EUR:
          return tracksIncomeSummary?.track_total_net_income_eur || 0;

        case Currency.BDT:
          return tracksIncomeSummary?.track_total_net_income_bdt || 0;

        default:
          return 0;
      }
    },
  };

  const getSecondTotal = (c: string) => {
    switch (c) {
      case Currency.USD:
        if (isClientDetails) return getFirstTotal.payable(Currency.USD);
        if (isClapAdminORClapManager(userDetails))
          return getFirstTotal.labelReceived(Currency.USD);
        return getFirstTotal.payable(Currency.USD);
      case Currency.EUR:
        if (isClientDetails) return getFirstTotal.payable(Currency.EUR);
        if (isClapAdminORClapManager(userDetails))
          return getFirstTotal.labelReceived(Currency.EUR);
        return getFirstTotal.payable(Currency.EUR);
      case Currency.BDT:
        if (isClientDetails) return getFirstTotal.payable(Currency.BDT);
        if (isClapAdminORClapManager(userDetails))
          return getFirstTotal.labelReceived(Currency.BDT);
        return getFirstTotal.payable(Currency.BDT);
      default:
        return 0;
    }
  };

  const getSecondTotalTitle = () => {
    if (isClientDetails) return "Total Payable";
    if (isClapAdminORClapManager(userDetails)) return "Label Received";
    return "Total Receivable";
  };

  /*   const isFilterVisible = () => {
    if (isClientDetails || isAccounts(userDetails)) {
      if (collapsed) return true;
      return false;
    }
    return true;
  }; */

  /* FOR TRACKS DROPDOWN: LOAD ALL TRACKS ON FOCUS  */
  const [isFocused, setIsFocused] = useState(false);
  const handleFocus = () => {
    if (!isFocused) {
      dispatch(retrieveAllTracks({ clientId }));
      setIsFocused(true);
    }
  };

  useEffect(() => {
    if (downloadTracksInCSVPending) {
      message.loading("CSV Downloading ...", 0);
    }
    if (!downloadTracksInCSVPending && downloadedTracksInCSVResponse) {
      message.destroy();
      dispatch(resetDownloadTracksInCSV());
    }
  }, [downloadTracksInCSVPending]);

  // Handle failed shares response
  useEffect(() => {
    if (!failedSharesPending && failedShares !== null && isApiCallInProgress) {
      // Reset the API call flag
      setIsApiCallInProgress(false);

      if (failedShares.length > 0) {
        // Show confirmation modal if there are failed shares
        setConfirmVisible(true);
      } else {
        // No failed shares, proceed directly to invoice creation
        // Only open if tracksIncomeSummary is available
        if (tracksIncomeSummary) {
          setVisible(true);
        }
      }
    }
  }, [failedSharesPending, failedShares, isApiCallInProgress, tracksIncomeSummary]);

  const handleMenuClick: MenuProps["onClick"] = (e) => {
    if (e.key === "CSV") {
      dispatch(
        downloadTracksInCSV({
          ...(isClientDetails && { clientId }),
          ...(isAccounts(userDetails) && {
            clientId: userDetails.client,
          }),
          ...(!isClientDetails &&
            isClapAdminORClapManager(userDetails) && {
            csv_type: "ADMIN_REPORT",
          }),
          ...filterForm.getFieldsValue(),
          currency,
          dateRange,
        }),
      );
    }
  };

  const downloadMenu = (
    <Menu
      className="min-w-[100px] rounded-[3px]"
      onClick={handleMenuClick}
      items={[
        {
          label: (
            <Space>
              <FileTextOutlined />
              <div>CSV</div>
            </Space>
          ),
          key: "CSV",
        },
        {
          label: (
            <PDFDownloadLink
              document={
                <PDFRenderedDoc
                  isClientDetails={isClientDetails || false}
                  userDetails={userDetails}
                  client={client}
                  tracksIncomeSummary={tracksIncomeSummary}
                  filterFields={{ ...filterForm.getFieldsValue() }}
                  apiData={{
                    allLabels,
                    allArtists,
                    allLyricists,
                    allComposers,
                    allTracks,
                    allPlatforms,
                    allDistributors,
                  }}
                  currency={currency}
                  dateRange={dateRange}
                  clientId={
                    (isClientDetails && clientId) ||
                    (isAccounts(userDetails) && userDetails.client)
                  }
                />
              }
              fileName="clap-reports.pdf"
            >
              <Space>
                <FilePdfOutlined />
                <div>PDF</div>
              </Space>
            </PDFDownloadLink>
          ),
          key: "PDF",
        },
      ]}
    />
  );

  const handleClientPayable = () => {
    if (clientPayableSort === "payable") setClientPayableSort("-payable");
    else setClientPayableSort("payable");
  };

  const controlledColumns = [
    {
      key: TrackIncomeKeys.PAYABLE,
      dataIndex: TrackIncomeKeys.PAYABLE,
      title: (
        <div
          className="flex justify-between cursor-pointer"
          onClick={handleClientPayable}
          aria-hidden="true"
        >
          <div>Client Payable</div>
          <div className="opacity-60">
            {clientPayableSort === "payable" ? (
              <ArrowUpOutlined />
            ) : (
              <ArrowDownOutlined />
            )}
          </div>
        </div>
      ),
      position: 11,
      showSorterTooltip: false,
      render: (value: any) => value,
    },
  ];

  const handleShowIncomeData = () => {
    setIsIncomeData(!isIncomeData);
  };

  return (
    <div className={`${isClientDetails && "pb-24"}`}>
      {!isClientDetails && <div className="text-heading20 mb-6">Reports</div>}
      {isAccounts(userDetails) && (
        <div className="mb-4">
          <ClientProfile client={client} type={ROLE.ACCOUNTS} />
        </div>
      )}
      <div className="border border-solid border-light-silver pt-3">
        <div className="resetborder-b border-light-silver px-4 pb-3">
          <span className="text-[16px] font-semibold">Filter Income</span>
        </div>
        <Form form={filterForm} layout="vertical" name="filterForm">
          <div className="px-4 pt-4">
            <div>{getSelectFields("Label", allLabels)}</div>
            <div className="flex-none md:flex items-center">
              <div className="flex-1">
                {getSelectFields("Artist", allArtists)}
              </div>
              <div className="hidden md:block px-5 pt-2.5 opacity-50">or</div>
              <div className="flex-1">
                {getSelectFields("Lyricist", allLyricists)}
              </div>
              <div className="hidden md:block px-5 pt-2.5 opacity-50">or</div>
              <div className="flex-1">
                {getSelectFields("Composer", allComposers)}
              </div>
            </div>
            <div className="flex-none md:flex items-center">
              <div className="flex-1">
                <Form.Item name="track" label="Track" className="mb-[6px]">
                  <Select
                    mode="multiple"
                    placeholder="Select Track"
                    className="w-full"
                    // showSearch
                    allowClear
                    filterOption={(inputValue, option) =>
                      option?.props.children
                        .toString()
                        .toLowerCase()
                        .includes(inputValue.toLowerCase())
                    }
                    onFocus={handleFocus}
                    loading={allTracksLoading as boolean}
                  >
                    {allTracks?.map((option: any) => (
                      <Option key={option.id} value={option.id}>
                        {option.title}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </div>
              <div className="hidden md:block px-5 pt-2.5 opacity-50">and</div>
              <div className="flex-1">
                {getSelectFields("Platform", allPlatforms)}
              </div>
              <div className="hidden md:block px-5 pt-2.5 opacity-50">and</div>
              <div className="flex-1">
                {getSelectFields("Distributor", allDistributors)}
              </div>
            </div>
          </div>
          <div className="px-4 pt-3 resetborder-t border-light-silver">
            <Form
              form={form}
              layout="vertical"
              name="form"
              className="flex-none md:flex justify-between items-center"
              validateMessages={validateMessages}
            >
              <div className="flex-1">
                <Form.Item
                  name="income_period"
                  label="Income Period"
                  rules={[{ required: true }]}
                  validateStatus={dateRangeExceed ? "error" : undefined}
                  help={
                    dateRangeExceed
                      ? "Range exceed. Max selection 1 year."
                      : undefined
                  }
                  initialValue={[
                    moment(previousQuarterStartDate),
                    moment(previousQuarterEndDate),
                  ]}
                >
                  <RangePicker
                    className="w-full"
                    onChange={onRangeChange}
                    onOpenChange={onRangeOpenChange}
                    status={
                      !dateRangeOpen && (!dateRange.from || dateRangeExceed)
                        ? "error"
                        : undefined
                    }
                    allowClear={false}
                  />
                </Form.Item>
              </div>
              <div className="hidden md:block px-5 pt-2.5 opacity-50">and</div>
              <div className="flex-1">
                <Form.Item
                  name="currency"
                  label="Currency"
                  initialValue={Currency.EUR}
                >
                  <Select
                    placeholder="Please select"
                    className="w-full"
                    onChange={onCurrencyChange}
                  >
                    {currencies.map((item: any) => (
                      <Select.Option key={item.id} value={item.name}>
                        {item.name}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </div>
              <div className="invisible px-5 pt-2.5">and</div>
              <div className="flex-1 pb-3 md:pb-0">
                <div className="flex gap-x-1.5 md:justify-end md:items-end md:pt-2.5">
                  <Button type="link" onClick={resetFilters}>
                    Reset
                  </Button>
                  <Button
                    type="primary"
                    disabled={
                      dateRangeExceed || !dateRange.from || !dateRange.to
                    }
                    onClick={onApplyFilters}
                  >
                    Apply Filters
                  </Button>
                </div>
              </div>
            </Form>
          </div>
        </Form>
      </div>
      <div className="my-5 border border-solid border-light-silver bg-lotion">
        <div className="resetborder-b border-light-silver px-4 py-3">
          <span className="text-[16px] font-semibold">
            {isClientDetails ? "Payable" : "Income"} based on filter
            <span className="pl-3">
              {tracksIncomeSummaryPending && (
                <LoadingOutlined style={{ fontSize: 14, color: "#0055AC" }} />
              )}
            </span>
          </span>
        </div>
        <div className="p-4 flex justify-between items-center">
          <div className="flex gap-6">
            <div className="space-y-2">
              <div>Total Income ({currency2})</div>
              <div>
                {getSecondTotalTitle()} ({currency2})
              </div>
            </div>
            <div className="space-y-2">
              <div className="text-heading21 text-metallic-orange -mt-1">
                {tracksIncomeSummaryPending
                  ? ""
                  : getFirstTotal.income(currency2)}
              </div>
              <div className="text-heading21 text-metallic-orange -mt-1">
                {tracksIncomeSummaryPending ? "" : getSecondTotal(currency2)}
              </div>
            </div>
          </div>
          <div className="flex self-end gap-2">
            <Button type="link" onClick={handleShowIncomeData}>
              {isIncomeData ? "Hide" : "Show"} Income Data
            </Button>
            {(isClapAdminORClapManager(userDetails) ||
              (isAccounts(userDetails) && client?.has_export_permission)) && (
                <div className="flex gap-x-2">
                  {/* <PDFDownloadLink
                  document={<PDFRenderedDoc />}
                  fileName="clap_report.pdf"
                >
                  {({ blob, url, loading, error }) =>
                    loading ? "Loading document..." : "Download now!"
                  }
                </PDFDownloadLink> */}
                  <Dropdown
                    overlay={downloadMenu}
                    placement="bottomRight"
                    trigger={["click"]}
                    disabled={tracksIncomeSummaryPending as boolean}
                  >
                    <Button>
                      <Space>
                        Export
                        <DownOutlined />
                      </Space>
                    </Button>
                  </Dropdown>
                </div>
              )}
            {isClientDetails && (
              <div>
                <Button
                  type="primary"
                  onClick={createInvoice}
                  loading={failedSharesPending as boolean}
                  disabled={
                    !client ||
                    checkPayableZero()
                  }
                >
                  Create Invoice
                </Button>
                {visible && (
                  <CreateInvoiceModal
                    visible={visible}
                    onCancel={hideModal}
                    form={form}
                    filterForm={filterForm}
                    client={client}
                  />
                )}
                {confirmVisible && (
                  <InvoiceConfirmationModal
                    visible={confirmVisible}
                    onCancel={() => {
                      setConfirmVisible(false);
                      dispatch(resetClientFailedShares());
                    }}
                    onProcess={handleCreateInvoice}
                    clientId={clientId || ""}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      {/* <PDFViewer style={{ width: "100%", height: "100vh" }}>
        <PDFRenderedDoc
          isClientDetails={isClientDetails || false}
          userDetails={userDetails}
          client={client}
          tracksResponse={tracksResponse}
          filterFields={{ ...filterForm.getFieldsValue() }}
          apiData={{
            allLabels,
            allArtists,
            allLyricists,
            allComposers,
            allTracks,
            allPlatforms,
            allDistributors
          }}
          currency={currency}
          dateRange={dateRange}
          clientId={
            (isClientDetails && clientId) ||
            (isAccounts(userDetails) && userDetails.client)
          }
        />
      </PDFViewer> */}
      {isIncomeData && (
        <>
          <div className="mt-8 mb-4">
            <SearchComponent handleSearch={handleSearch} />
          </div>
          <Table
            rowKey={(record: any) => {
              return record.id;
            }}
            columns={tableUtils.buildColumns(
              tracks,
              columns,
              null,
              isClientDetails && tracks.length && controlledColumns,
            )}
            dataSource={tracks}
            pagination={false}
            size="middle"
            scroll={{
              ...(globalUtils.checkIsScroll(tracks) && { y: 474 }),
              x: 1024,
            }}
            className="ant_table"
            loading={{
              spinning: tablePending as boolean,
              indicator: <LoadingOutlined style={{ fontSize: 34 }} />,
            }}
          />
          <div className="text-right mt-4">
            <Pagination
              current={page}
              onChange={handlePageChange}
              total={tracksResponse?.count}
              showSizeChanger={false}
              pageSize={PageSize.FIFTY}
            />
          </div>
        </>
      )}
    </div>
  );
}

export default ClapIncomes;
